import React from "react";
import { ShieldCheck, LogIn } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Tag, EmptyState } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/vda";

function loginTone(action = "") {
  if (action.includes("failed") || action.includes("denied")) return "red";
  if (action.includes("logout")) return "muted";
  return "green";
}

export default function LoginAudit() {
  const { data, loading } = useAsync(() => api.adminLoginAudit(), []);
  if (loading) return <StaffShell><Spinner /></StaffShell>;

  const login = data?.login_events || [];
  const app = data?.app_events || [];

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Security</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-5">Login &amp; security audit</h1>

      <Panel className="p-0 overflow-hidden mb-6" data-testid="login-audit-panel">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-semibold">
          <LogIn className="h-4 w-4 text-primary" /> Authentication events
          <Tag tone="muted">{login.length}</Tag>
        </div>
        {login.length === 0 ? <div className="p-5"><EmptyState title="No authentication events" hint="Supabase auth audit log is empty or unavailable." /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Action</th>
                  <th className="px-4 py-2 font-medium">Actor</th>
                  <th className="px-4 py-2 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {login.map((e) => (
                  <tr key={e.id} className="border-t border-border" data-testid={`login-event-${e.id}`}>
                    <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                    <td className="px-4 py-2"><Tag tone={loginTone(e.action)}>{e.action || "—"}</Tag></td>
                    <td className="px-4 py-2">{e.actor_username || e.actor_id || "—"}</td>
                    <td className="px-4 py-2 text-muted-foreground">{e.ip_address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel className="p-0 overflow-hidden" data-testid="app-audit-panel">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-semibold">
          <ShieldCheck className="h-4 w-4 text-primary" /> Operational &amp; security events
          <Tag tone="muted">{app.length}</Tag>
        </div>
        {app.length === 0 ? <div className="p-5"><EmptyState title="No operational events" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                  <th className="px-4 py-2 font-medium">When</th>
                  <th className="px-4 py-2 font-medium">Action</th>
                  <th className="px-4 py-2 font-medium">Entity</th>
                  <th className="px-4 py-2 font-medium">Actor</th>
                  <th className="px-4 py-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {app.map((e) => (
                  <tr key={e.id} className="border-t border-border" data-testid={`app-event-${e.id}`}>
                    <td className="px-4 py-2 tabular-nums whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                    <td className="px-4 py-2">{(e.action || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2 text-muted-foreground">{(e.entity_type || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2">{e.actor_name || "—"}</td>
                    <td className="px-4 py-2 text-muted-foreground">{e.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </StaffShell>
  );
}
